import { store } from '@/store';
import api from '@api/Api';
import UserApi from '@api/UserApi';

export default {

    getProject(id) {
        return api().get(`/projects/${id}`).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProject(id, params) {
        return api().put(`/projects/${id}`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    completeSetup(id) {
        return api().put(`/projects/${id}/setup/complete`).then((response) => {
            const project = response.data.data;
            const applyProject = () => {
                store.commit('setProject', project);
                store.commit('setUserRelation', {
                    key: 'selected_project',
                    value: project,
                });
            };
            // Refresh user (trial restart updates billing). setUser can replace
            // state.project from selected_project — re-apply setup payload after.
            // Always apply project even if getUser fails so gating isn't stuck.
            return UserApi.getUser().catch(() => null).then(() => {
                applyProject();
                return response;
            });
        });
    },

    updateProjectSpace(id, params) {
        return api().put(`/projects/${id}/space`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectSlug(id, params) {
        return api().put(`/projects/${id}/slug`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectOverrides(id, params) {
        return api().put(`/projects/${id}/overrides`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectMeta(id, params) {
        return api().put(`/projects/${id}/meta`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    generateProjectMeta(id, params) {
        return api().put(`/projects/${id}/meta/generate`, params);
    },

    updateProjectRobots(id, params) {
        return api().put(`/projects/${id}/robots`, params);
    },

    createProjectCname(id, params) {
        return api().post(`/projects/${id}/cname`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectCname(id, params) {
        return api().put(`/projects/${id}/cname`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    deleteProjectCname(id) {
        return api().delete(`/projects/${id}/cname`).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectBranding(id, params) {
        return api().put(`/projects/${id}/branding`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectRatings(id, params) {
        return api().put(`/projects/${id}/ratings`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectEmail(id, params) {
        return api().put(`/projects/${id}/email`, params).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    updateProjectEmailAppearance(id, params) {
        return api().put(`/projects/${id}/email/appearance`, params);
    },

    duplicateProject(id, params) {
        return api().post(`/projects/${id}/duplicate`, params).then((response) => {
            store.commit('setUser', response.data.data);
            return response;
        });
    },

    deleteProject(id, params) {
        return api().delete(`/projects/${id}`, params).then((response) => {
            const user = response.data.data;
            if (user && Array.isArray(user.projects)) {
                user.projects = user.projects.filter((project) => project.id !== id);
            }
            store.commit('setUser', user);
            return response;
        });
    },

    /**
     * Suspend / Unsuspend
     */

    suspendProject(id) {
        return api().put(`/projects/${id}/suspend`);
    },

    unsuspendProject(id) {
        return api().put(`/projects/${id}/unsuspend`);
    },

    /**
     * Activate / Deactivate
     */

    activateProject(id) {
        return api().put(`/projects/${id}/activate`).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    deactivateProject(id) {
        return api().put(`/projects/${id}/deactivate`).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    /**
     * Tasks
     */

    completeTask(id, task) {
        return api().put(`/projects/${id}/tasks`, { task: task }).then((response) => {
            store.commit('setProject', response.data.data);
            return response;
        });
    },

    /**
     * Key
     */

    getKey(id) {
        return api().get(`/projects/${id}/key`);
    },

    deleteKey(id) {
        return api().delete(`/projects/${id}/key`);
    },

    /**
     * Signatures
     */

    getSignatures(id) {
        return api().get(`/projects/${id}/signatures`);
    },

    createSignature(id, params) {
        return api().post(`/projects/${id}/signatures`, params);
    },

    previewSignature(id, params) {
        return api().post(`/projects/${id}/signatures/preview`, params);
    },

    /**
     * Share templates
     */

    getShareTemplates(id) {
        return api().get(`/projects/${id}/share-templates`);
    },

    createShareTemplate(id, params) {
        return api().post(`/projects/${id}/share-templates`, params);
    },

    /**
     * Sources
     */

    getSources(id) {
        return api().get(`/projects/${id}/sources`);
    },

    createSource(id, params) {
        return api().post(`/projects/${id}/sources`, params);
    },

    /**
     * Tags
     */

    getTags(id) {
        return api().get(`/projects/${id}/tags`);
    },

    createTag(id, params) {
        return api().post(`/projects/${id}/tags`, params);
    },

    /**
     * Tag Rules
     */

    getTagRules(id) {
        return api().get(`/projects/${id}/tag-rules`);
    },

    createTagRule(id, params) {
        return api().post(`/projects/${id}/tag-rules`, params);
    },

    /**
     * Locations
     */

    getLocations(id, params) {
        return api().get(`/projects/${id}/locations`, { params: params });
    },

    createLocation(id, params) {
        return api().post(`/projects/${id}/locations`, params);
    },

    /**
     * Domain
     */

    createDomain(id, params) {
        return api().post(`/projects/${id}/domains`, params);
    },

    /**
     * Tenplates
     */

    getTemplates(id) {
        return api().get(`/projects/${id}/templates`);
    },

    updateProjectTemplatesCta(id, params) {
        return api().put(`/projects/${id}/templates/cta`, params);
    },

    /**
     * Customers
     */

    getCustomers(id, params) {
        return api().get(`/projects/${id}/customers`, { params: params });
    },

    getCustomersUnprocessed(id) {
        return api().get(`/projects/${id}/customers/unprocessed`);
    },

    createCustomer(id, params) {
        return api().post(`/projects/${id}/customers`, params);
    },

    createCustomersWithCSV(id, params) {
        return api().post(`/projects/${id}/customers/upload`, params);
    },

    downloadCustomerTemplate(id) {
        return api().get(`/projects/${id}/customers/template`);
    },

    tagCustomers(id, params) {
        return api().post(`/projects/${id}/customers/tag`, params);
    },

    updateCustomers(id, params) {
        return api().post(`/projects/${id}/customers/update`, params);
    },

    unsubscribeCustomers(id, params) {
        return api().post(`/projects/${id}/customers/unsubscribe`, params);
    },

    deleteCustomers(id, params) {
        return api().post(`/projects/${id}/customers/delete`, params);
    },

    /**
     * Asks
     */

    getAsks(id, params) {
        return api().get(`/projects/${id}/asks`, { params: params });
    },

    createAsk(id, params) {
        return api().post(`/projects/${id}/asks`, params);
    },

    createAsks(id, params) {
        return api().post(`/projects/${id}/asks/bulk`, params);
    },

    /**
     * Messages
     */

    getMessages(id, params) {
        return api().get(`/projects/${id}/messages`, { params: params });
    },

    getMessageStats(id, channel, params) {
        return api().get(`/projects/${id}/messages/stats/${channel}`, { params: params });
    },

    cancelMessages(id, params) {
        return api().put(`/projects/${id}/messages/cancel`, params);
    },

    /**
     * Integrations
     */

    getIntegrations(id) {
        return api().get(`/projects/${id}/integrations`);
    },

    createIntegration(id, slug, params) {
        return api().post(`/projects/${id}/integrations/${slug}`, params);
    },

    /**
     * Reviews
     */

    getReviews(id, params) {
        return api().get(`/projects/${id}/reviews`, { params: params });
    },

    getReviewsSummary(id, params) {
        return api().get(`/projects/${id}/reviews/summary`, { params: params });
    },

    getReviewsStats(id, params) {
        return api().get(`/projects/${id}/reviews/stats`, { params: params });
    },

    createReview(id, params) {
        return api().post(`/projects/${id}/reviews`, params);
    },

    createReviewsWithCSV(id, params) {
        return api().post(`/projects/${id}/reviews/upload`, params);
    },

    tagReviews(id, params) {
        return api().post(`/projects/${id}/reviews/tag`, params);
    },

    updateReviewsLocation(id, params) {
        return api().post(`/projects/${id}/reviews/update-location`, params);
    },

    updateReviewsSource(id, params) {
        return api().post(`/projects/${id}/reviews/update-source`, params);
    },

    hideReviews(id, params) {
        return api().post(`/projects/${id}/reviews/hide`, params);
    },

    deleteReviews(id, params) {
        return api().post(`/projects/${id}/reviews/delete`, params);
    },

    /**
     * OAuth Clients
     */

    getOAuthClients(id) {
        return api().get(`/projects/${id}/oauth/clients`);
    },

    deleteOAuthClient(id, clientId) {
        return api().delete(`/projects/${id}/oauth/clients/${clientId}`);
    },

    /**
     * Webhooks
     */

    getWebhooks(id) {
        return api().get(`/projects/${id}/webhooks`);
    },

    createWebhook(id, params) {
        return api().post(`/projects/${id}/webhooks`, params);
    },

    /**
     * Members
     */

    getMembers(id) {
        return api().get(`/projects/${id}/members`);
    },

    createMember(id, params) {
        return api().post(`/projects/${id}/members`, params);
    },

    /**
     * Dashboard
     */

    getDashboard(id, params) {
        return api().get(`/projects/${id}/dashboard`, { params: params });
    },

    /**
     * Forms
     */

    getForms(id, params) {
        return api().get(`/projects/${id}/forms`, { params: params });
    },

    createForm(id, params) {
        return api().post(`/projects/${id}/forms`, params);
    },

    deleteForms(id, params) {
        return api().post(`/projects/${id}/forms/delete`, params);
    },

    /**
     * Widgets
     */

    getWidgets(id, params) {
        return api().get(`/projects/${id}/widgets`, { params: params });
    },

    createWidget(id, params) {
        return api().post(`/projects/${id}/widgets`, params);
    },

    deleteWidgets(id, params) {
        return api().post(`/projects/${id}/widgets/delete`, params);
    },

    /**
     * Exports
     */

    exportCustomers(id, params) {
        return api().post(`/projects/${id}/customers/export`, params);
    },

    exportReviews(id, params) {
        return api().post(`/projects/${id}/reviews/export`, params);
    },

    /**
     * Scrapes
     */

    scrapeReviews(id, params) {
        return api().post(`/projects/${id}/reviews/scrape`, params);
    },

    /**
     * Assistant
     */

    getAssistant(id, params) {
        return api().get(`/projects/${id}/assistant`, { params });
    },

    generateReviews(id, params) {
        return api().post(`/projects/${id}/assistant/generate-reviews`, params);
    },

    /**
     * Analytics
     */

    getAnalytics(id, params) {
        return api().get(`/projects/${id}/analytics`, { params: params });
    },

    updateAnalyticsLayout(id, params) {
        return api().put(`/projects/${id}/analytics/layout`, params);
    },

    /**
     * DNS Records
     */

    downloadDnsRecords(id) {
        return api().get(`/projects/${id}/dns-records/download`);
    },

}